import React, { useMemo, useState } from 'react';
import Drawer from '../shared/Drawer';
import api from '../../utils/api';

/**
 * Submit Case Outcome Report — Scale-parity port of CaseOutcomeReportForm.
 *
 * Layout mirrors Scale's right-side drawer:
 *   1. Case Information (Associated Case Number / Title — read-only)
 *   2. Rate Yourself (5-star SVG row, gold #FFAC33)
 *   3. Settlement Status accordion (Settled / Not Settled / Future Mediation)
 *      - Settled: opening demand, opening offer, settlement amount, terms
 *      - Not Settled / Future: last demand, last offer, reason, follow-up
 *   4. Participants accordion (per plaintiff + defendant + their additional
 *      parties, with rating stars, designation, future-mediation likelihood,
 *      phone, notes, "Do not include in CRM" toggle)
 *   5. CAR Cases Outcome Report accordion (only when caseData.is_car_case)
 *      - 3 Likert questions, yes/no questions, free-text fields
 *   6. Footer: "You will not be able to edit the outcome later" + Save Outcome
 *
 * Submit POSTs the full payload — including participants and car_* keys — to
 * /cases/:id/outcome-report. The server packs car_* fields into a JSONB blob
 * and the participants object into participants JSONB.
 */

const SETTLEMENT_STATUSES = ['Settled', 'Not Settled', 'Future Mediation'];

const LIKERT_OPTIONS = [
  'Strongly disagree',
  'Disagree',
  'Neutral',
  'Agree',
  'Strongly agree',
];

const LIKERT_QUESTIONS = [
  { label: 'The parties seemed to understand the overall mediation process.', field: 'car_parties_understood_process' },
  { label: 'The parties were reasonably prepared for the mediation.', field: 'car_parties_prepared' },
  { label: 'The attorneys were reasonably prepared for the mediation.', field: 'car_attorneys_prepared' },
];

const NOT_SETTLED_REASONS = [
  'The mediation had a neutral impact',
  'I gave my opinion about settlement',
  'The mediation needed to occur later',
  'The parties made progress toward settlement',
  'The mediation aggravated the dispute',
  'The mediation helped the parties narrow their issues',
  'The mediation needed to occur earlier',
];

const LIKELIHOOD_OPTIONS = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

/* ── Star row (matches Scale's renderStars SVG path + colors) ──────────── */
function StarRow({ value, onChange, size = 24 }) {
  return (
    <div className="flex items-center gap-x-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          onClick={() => onChange(i + 1 === value ? 0 : i + 1)}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size + 1}
            viewBox="0 0 24 25"
            fill="none"
            style={{
              fill: i < value ? '#FFAC33' : '#D3D3D3',
              transition: 'fill 0.2s ease-in-out',
            }}
          >
            <path d="M17.9494 24.0466C17.6688 24.0466 17.3897 23.9605 17.1494 23.7889L11.5001 19.7376L5.85077 23.7889C5.61671 23.9573 5.33543 24.0475 5.04709 24.0465C4.75875 24.0455 4.4781 23.9534 4.24521 23.7834C4.01211 23.6144 3.83824 23.3761 3.74835 23.1026C3.65845 22.829 3.65711 22.5341 3.74452 22.2598L5.85147 15.4598L0.252854 11.5146C0.0208272 11.3434 -0.151489 11.1035 -0.239722 10.829C-0.327954 10.5545 -0.327636 10.2591 -0.238813 9.98477C-0.149288 9.71109 0.0239833 9.47246 0.256513 9.30261C0.489043 9.13276 0.769071 9.04028 1.05702 9.03825L7.99105 9.02783L10.1987 2.39658C10.2898 2.1234 10.4645 1.88581 10.6982 1.71745C10.9318 1.54909 11.2125 1.4585 11.5004 1.4585C11.7884 1.4585 12.0691 1.54909 12.3027 1.71745C12.5363 1.88581 12.7111 2.1234 12.8022 2.39658L14.9723 9.02783L21.9417 9.03825C22.23 9.03987 22.5105 9.1323 22.7432 9.30241C22.976 9.47251 23.1493 9.71164 23.2384 9.98582C23.3275 10.26 23.3279 10.5553 23.2396 10.8297C23.1514 11.1042 22.9789 11.3438 22.7466 11.5146L17.148 15.4598L19.2549 22.2598C19.3425 22.534 19.3413 22.829 19.2516 23.1025C19.1618 23.3761 18.988 23.6143 18.7549 23.7834C18.5213 23.9547 18.2391 24.047 17.9494 24.0466Z" />
          </svg>
        </span>
      ))}
    </div>
  );
}

/* ── Accordion shell — matches Scale's gray header + chevron ──────────── */
function Chevron({ up }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="inline-block">
      <path
        d={up ? 'M3 9l4-4 4 4' : 'M3 5l4 4 4-4'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AccordionShell({ title, open, onToggle, children, headerColor = 'text-gray-900', borderColor }) {
  return (
    <div className={`rounded-lg ${borderColor ? `border ${borderColor}` : ''}`}>
      <button
        type="button"
        className={`w-full text-left px-4 py-3 font-medium bg-gray-100 hover:bg-gray-200 ${borderColor ? 'rounded-t-md' : 'rounded-md'}`}
        onClick={onToggle}
      >
        <span className={`font-bold ${headerColor}`}>{title}</span>
        <span className="float-right font-bold text-gray-700">
          <Chevron up={open} />
        </span>
      </button>
      <div className={`transition-all duration-300 ${open ? 'max-h-[5000px] opacity-100' : 'max-h-0 overflow-hidden opacity-0'}`}>
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

/* ── Form primitives matching Scale's FormItem + Input + FormNumericInput ─ */
function FieldLabel({ children }) {
  return <label className="block text-[#111927] text-sm font-medium mb-1.5">{children}</label>;
}

function FormItem({ label, error, children }) {
  return (
    <div className="mb-4">
      {label && <FieldLabel>{label}</FieldLabel>}
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function CurrencyInput({ value, onChange, placeholder }) {
  // Mirrors FormNumericInput: $ prefix + thousand-separator on display.
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    onChange(raw);
  };
  const display = value ? Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '';
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
      <input
        type="text"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-md pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#115E59] focus:border-[#115E59]"
      />
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text', min }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#115E59] focus:border-[#115E59]"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#115E59] focus:border-[#115E59]"
    />
  );
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label className="flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mr-2 w-4 h-4 rounded border-gray-300 text-[#115E59] focus:ring-[#115E59]"
      />
      <span className="text-[#111927]">{label}</span>
    </label>
  );
}

function Radio({ name, value, current, onChange, label }) {
  return (
    <label className="flex items-center cursor-pointer">
      <input
        type="radio"
        name={name}
        value={value}
        checked={current === value}
        onChange={() => onChange(value)}
        className="mr-2"
      />
      <span className="text-[#111927]">{label}</span>
    </label>
  );
}

/* ── Settlement Status section ─────────────────────────────────────────── */
function SettlementStatusSection({ values, setValue, errors }) {
  const [open, setOpen] = useState(true);

  const renderInputs = () => {
    if (values.status === 'Settled') {
      return (
        <>
          <FormItem label="Plaintiff Opening Demand" error={errors.plaintiff_opening_demand}>
            <CurrencyInput value={values.plaintiff_opening_demand} onChange={(v) => setValue('plaintiff_opening_demand', v)} placeholder="Enter Plaintiff Opening Demand" />
          </FormItem>
          <FormItem label="Defendant Opening Offer" error={errors.defendant_opening_offer}>
            <CurrencyInput value={values.defendant_opening_offer} onChange={(v) => setValue('defendant_opening_offer', v)} placeholder="Enter Defendant Opening Offer" />
          </FormItem>
          <FormItem label="Settlement Amount" error={errors.settlement_amount}>
            <CurrencyInput value={values.settlement_amount} onChange={(v) => setValue('settlement_amount', v)} placeholder="Enter Settlement Amount" />
          </FormItem>
          <FormItem label="Terms" error={errors.term}>
            <TextArea value={values.term} onChange={(v) => setValue('term', v)} placeholder="Enter Term" />
          </FormItem>
        </>
      );
    }
    if (values.status === 'Not Settled' || values.status === 'Future Mediation') {
      const minDate = values.status === 'Not Settled' ? new Date().toISOString().slice(0, 10) : undefined;
      return (
        <>
          <FormItem label="Plaintiff Last Demand" error={errors.plaintiff_last_demand}>
            <CurrencyInput value={values.plaintiff_last_demand} onChange={(v) => setValue('plaintiff_last_demand', v)} placeholder="Enter Plaintiff Last Demand" />
          </FormItem>
          <FormItem label="Defendant Last Offer" error={errors.defendant_last_offer}>
            <CurrencyInput value={values.defendant_last_offer} onChange={(v) => setValue('defendant_last_offer', v)} placeholder="Enter Defendant Last Offer" />
          </FormItem>
          <FormItem label="Reason" error={errors.reason}>
            <TextArea value={values.reason} onChange={(v) => setValue('reason', v)} placeholder="Enter Reason" />
          </FormItem>
          <FormItem>
            <Checkbox checked={values.follow_up} onChange={(v) => setValue('follow_up', v)} label="Follow Up" />
          </FormItem>
          {values.follow_up && (
            <FormItem label="Follow up Date" error={errors.follow_up_date}>
              <TextInput type="date" value={values.follow_up_date} onChange={(v) => setValue('follow_up_date', v)} placeholder="Follow up date" min={minDate} />
            </FormItem>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <AccordionShell title="Settlement Status" open={open} onToggle={() => setOpen(!open)}>
      <div className="py-2 px-2">
        {SETTLEMENT_STATUSES.map((s) => (
          <label className="flex items-center mt-2" key={s}>
            <input
              type="checkbox"
              name="status"
              checked={values.status === s}
              onChange={() => setValue('status', values.status === s ? '' : s)}
              className="mr-2 w-4 h-4 rounded border-gray-300 text-[#115E59] focus:ring-[#115E59]"
            />
            <span className="text-[#111927]">{s}</span>
          </label>
        ))}
      </div>
      <div className="mt-2">{renderInputs()}</div>
    </AccordionShell>
  );
}

/* ── Per-participant accordion item (rating, designation, etc.) ───────── */
function ParticipantItem({ participant, values, setParticipantValue, ensureBaseValues }) {
  const [open, setOpen] = useState(false);
  const id = participant.id || participant.client_id;
  const data = values.participants?.[id] || {};

  const toggle = () => {
    if (!open) ensureBaseValues(participant);
    setOpen(!open);
  };

  const set = (field, val) => setParticipantValue(id, field, val);

  return (
    <div className="rounded-lg mb-3">
      <button
        type="button"
        className="w-full text-left px-4 py-3 font-medium bg-gray-100 hover:bg-gray-200 rounded-md"
        onClick={toggle}
      >
        <div className="flex flex-col">
          <div className="flex items-center">
            <span className="px-2 text-gray-900 font-bold">{participant.name || '—'}</span>
            {participant.designation && (
              <span className="text-xs px-2 text-[#111927]">{participant.designation}</span>
            )}
            <span className="float-right font-bold ml-auto text-gray-700">
              <Chevron up={open} />
            </span>
          </div>
          <div className="px-2 mt-1">
            <span className="text-xs text-gray-500 capitalize">Role: {participant.type || ''}</span>
          </div>
        </div>
      </button>
      <div className={`transition-all duration-300 ${open ? 'max-h-[1200px] opacity-100' : 'max-h-0 overflow-hidden opacity-0'}`}>
        <div className="px-2 pt-2">
          <FormItem>
            <Checkbox
              checked={data.is_part_crm === false}
              onChange={(checked) => set('is_part_crm', !checked)}
              label="Do not include in CRM"
            />
          </FormItem>

          {data.is_part_crm !== false && (
            <>
              <div className="w-full flex items-start flex-col gap-y-1 mt-4">
                <span className="text-sm font-semibold">Rating</span>
                <div className="flex items-center w-full gap-2">
                  <div className="w-2/3 pb-2 flex items-center justify-start gap-x-2">
                    <StarRow value={data.rating || 0} onChange={(v) => set('rating', v)} />
                  </div>
                </div>
              </div>

              <FormItem label="Likelihood of Future Mediation">
                <select
                  value={data.future_mediation || ''}
                  onChange={(e) => set('future_mediation', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#115E59] focus:border-[#115E59]"
                >
                  <option value="">Select</option>
                  {LIKELIHOOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </FormItem>

              <FormItem label="Phone Number">
                <TextInput
                  type="tel"
                  value={data.phone_number || ''}
                  onChange={(v) => set('phone_number', v)}
                  placeholder="+1 (555) 555-5555"
                />
              </FormItem>

              <FormItem label="Designation">
                <TextInput
                  value={data.designation || ''}
                  onChange={(v) => set('designation', v)}
                  placeholder="Enter designation"
                />
              </FormItem>

              <FormItem label="Notes">
                <TextArea
                  value={data.notes || ''}
                  onChange={(v) => set('notes', v)}
                  placeholder="Enter notes"
                />
              </FormItem>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── CAR section (Likert + yes/no + open-text fields) ─────────────────── */
function CarSection({ values, setValue, errors }) {
  const [open, setOpen] = useState(true);
  const reasons = values.car_not_settled_reasons || [];

  const toggleReason = (reason) => {
    const next = reasons.includes(reason)
      ? reasons.filter((r) => r !== reason)
      : [...reasons, reason];
    setValue('car_not_settled_reasons', next);
  };

  const showLikertError =
    !!errors.car_parties_understood_process ||
    !!errors.car_parties_prepared ||
    !!errors.car_attorneys_prepared;

  return (
    <AccordionShell
      title="CAR Cases Outcome Report"
      open={open}
      onToggle={() => setOpen(!open)}
      headerColor=""
      borderColor="border-gray-200"
    >
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="font-semibold text-[#111927] mb-4">Respond to each statement if you Agree or Disagree</p>
          <div className="space-y-4">
            {LIKERT_QUESTIONS.map((q) => (
              <div key={q.field} className="pb-3 border-b border-gray-100 last:border-b-0 last:pb-0">
                <p className="text-sm text-[#111927] mb-2">{q.label}</p>
                <div className="flex flex-wrap gap-3">
                  {LIKERT_OPTIONS.map((opt) => (
                    <label key={opt} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={q.field}
                        value={opt}
                        checked={values[q.field] === opt}
                        onChange={() => setValue(q.field, opt)}
                        className="mr-1.5"
                      />
                      <span className="text-xs text-[#727B88] whitespace-nowrap">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {showLikertError && (
            <p className="text-red-500 text-xs mt-2">Please respond to all statements above.</p>
          )}
        </div>

        <FormItem label="All parties necessary for settlement were in attendance." error={errors.car_all_parties_in_attendance}>
          <div className="flex items-center gap-4 mt-1">
            <Radio name="car_all_parties_in_attendance" value="yes" current={values.car_all_parties_in_attendance} onChange={(v) => setValue('car_all_parties_in_attendance', v)} label="Yes" />
            <Radio name="car_all_parties_in_attendance" value="no" current={values.car_all_parties_in_attendance} onChange={(v) => setValue('car_all_parties_in_attendance', v)} label="No" />
          </div>
        </FormItem>

        <FormItem label="Did the parties (or their counsel) submit written statements or briefs in advance of the mediation?" error={errors.car_written_statements_submitted}>
          <div className="flex items-center gap-4 mt-1">
            <Radio name="car_written_statements_submitted" value="yes" current={values.car_written_statements_submitted} onChange={(v) => setValue('car_written_statements_submitted', v)} label="Yes" />
            <Radio name="car_written_statements_submitted" value="no" current={values.car_written_statements_submitted} onChange={(v) => setValue('car_written_statements_submitted', v)} label="No" />
          </div>
        </FormItem>

        <FormItem label="Additional explanation">
          <TextArea value={values.car_additional_explanation || ''} onChange={(v) => setValue('car_additional_explanation', v)} placeholder="Enter additional explanation (optional)" />
        </FormItem>

        <FormItem label="How much time did you spend in pre-mediation?" error={errors.car_pre_mediation_time}>
          <TextInput value={values.car_pre_mediation_time || ''} onChange={(v) => setValue('car_pre_mediation_time', v)} placeholder="Enter pre-mediation time" />
        </FormItem>

        <FormItem label="How many hours was the mediation?" error={errors.car_mediation_hours}>
          <TextInput value={values.car_mediation_hours || ''} onChange={(v) => setValue('car_mediation_hours', v)} placeholder="Enter mediation hours" />
        </FormItem>

        <FormItem label="In which format was the mediation conducted?" error={errors.car_mediation_format}>
          <div className="flex flex-col gap-2 mt-1">
            <Radio name="car_mediation_format" value="online" current={values.car_mediation_format} onChange={(v) => setValue('car_mediation_format', v)} label="Online" />
            <Radio name="car_mediation_format" value="in-person" current={values.car_mediation_format} onChange={(v) => setValue('car_mediation_format', v)} label="In-Person" />
            <Radio name="car_mediation_format" value="other" current={values.car_mediation_format} onChange={(v) => setValue('car_mediation_format', v)} label="Other" />
            {values.car_mediation_format === 'other' && (
              <div className="ml-6 mt-1">
                <TextInput value={values.car_mediation_format_other || ''} onChange={(v) => setValue('car_mediation_format_other', v)} placeholder="Please specify" />
              </div>
            )}
          </div>
        </FormItem>

        <FormItem label="How many hours did you bill the parties for the mediation?" error={errors.car_hours_billed}>
          <TextInput value={values.car_hours_billed || ''} onChange={(v) => setValue('car_hours_billed', v)} placeholder="Enter hours billed" />
        </FormItem>

        <FormItem label="Did the dispute settle?" error={errors.car_dispute_settled}>
          <div className="flex items-center gap-4 mt-1">
            <Radio name="car_dispute_settled" value="yes" current={values.car_dispute_settled} onChange={(v) => setValue('car_dispute_settled', v)} label="Yes" />
            <Radio name="car_dispute_settled" value="no" current={values.car_dispute_settled} onChange={(v) => setValue('car_dispute_settled', v)} label="No" />
          </div>
        </FormItem>

        {values.car_dispute_settled === 'no' && (
          <FormItem label="If no, why didn't it settle? Select all that apply:" error={errors.car_not_settled_reasons}>
            <div className="flex flex-col gap-2 mt-1">
              {NOT_SETTLED_REASONS.map((r) => (
                <Checkbox key={r} checked={reasons.includes(r)} onChange={() => toggleReason(r)} label={r} />
              ))}
            </div>
          </FormItem>
        )}

        {values.car_mediation_format === 'online' && (
          <FormItem label="If online, which platform was used and impact on outcome">
            <TextArea value={values.car_online_platform_impact || ''} onChange={(v) => setValue('car_online_platform_impact', v)} placeholder="Enter platform used and its impact on the outcome" />
          </FormItem>
        )}

        <FormItem label="Please provide any other feedback">
          <TextArea value={values.car_other_feedback || ''} onChange={(v) => setValue('car_other_feedback', v)} placeholder="Enter any other feedback (optional)" />
        </FormItem>
      </div>
    </AccordionShell>
  );
}

/* ── Top-level form ────────────────────────────────────────────────────── */
function buildInitialValues(caseData) {
  return {
    plaintiff_opening_demand: caseData?.casemetadata?.plaintiff_demand || '',
    defendant_opening_offer: caseData?.casemetadata?.defendant_demand || '',
    settlement_amount: '',
    term: '',
    follow_up: false,
    follow_up_date: '',
    reason: '',
    plaintiff_last_demand: '',
    defendant_last_offer: '',
    participants: {},
    status: 'Settled',
    rating: 0,
    // CAR fields — empty defaults; only persisted if rendered.
    car_parties_understood_process: '',
    car_parties_prepared: '',
    car_attorneys_prepared: '',
    car_all_parties_in_attendance: '',
    car_written_statements_submitted: '',
    car_additional_explanation: '',
    car_pre_mediation_time: '',
    car_mediation_hours: '',
    car_mediation_format: '',
    car_mediation_format_other: '',
    car_hours_billed: '',
    car_dispute_settled: '',
    car_not_settled_reasons: [],
    car_online_platform_impact: '',
    car_other_feedback: '',
  };
}

function mergeParticipants(caseData) {
  // Mirrors Scale's mergedParticipants: defendant + plaintiff + each side's
  // additionalParties, tagged with `type`. Falls back to the main party row
  // when no participants list exists (case-manager keeps participants on the
  // main client row for the simple two-party case).
  const out = [];
  const push = (p, type) => {
    if (!p) return;
    out.push({
      id: p.id ?? p.client_id ?? `${type}-${out.length}`,
      client_id: p.client_id || null,
      name: p.name || '',
      email: p.email || '',
      designation: p.designation || p.role || '',
      type,
    });
  };
  if (caseData?.defendant) {
    push(caseData.defendant, 'defendant');
    (caseData.defendant.additionalParties || []).forEach((p) => push(p, 'defendant'));
  }
  if (caseData?.plaintiff) {
    push(caseData.plaintiff, 'plaintiff');
    (caseData.plaintiff.additionalParties || []).forEach((p) => push(p, 'plaintiff'));
  }
  return out;
}

export default function CaseOutcomeReportModal({ isOpen, onClose, caseData, onSubmitted, onToast }) {
  const [values, setValues] = useState(() => buildInitialValues(caseData));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const mergedParticipants = useMemo(() => mergeParticipants(caseData), [caseData]);
  const isCarPortal = !!caseData?.is_car_case;

  const setValue = (field, val) => setValues((prev) => ({ ...prev, [field]: val }));

  const ensureBaseValues = (participant) => {
    const id = participant.id || participant.client_id;
    setValues((prev) => {
      if (prev.participants[id]) return prev;
      return {
        ...prev,
        participants: {
          ...prev.participants,
          [id]: {
            participantId: id,
            client_id: participant.client_id,
            name: participant.name,
            email: participant.email,
            designation: participant.designation,
            type: participant.type,
            phone_number: '',
            rating: 0,
            future_mediation: '',
            notes: '',
            is_part_crm: true,
          },
        },
      };
    });
  };

  const setParticipantValue = (id, field, val) => {
    setValues((prev) => ({
      ...prev,
      participants: {
        ...prev.participants,
        [id]: { ...(prev.participants[id] || {}), [field]: val },
      },
    }));
  };

  const reset = () => {
    setValues(buildInitialValues(caseData));
    setErrors({});
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const validate = () => {
    const errs = {};
    if (!values.status) errs.status = 'Select a settlement status';
    if (values.status === 'Not Settled' || values.status === 'Future Mediation') {
      if (values.follow_up && !values.follow_up_date) errs.follow_up_date = 'Pick a follow-up date';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/cases/${caseData.id}/outcome-report`, values);
      const report = res?.data?.data || res?.data;
      onToast?.({ type: 'success', title: 'Changes Saved', message: 'Your information updated successfully' });
      onSubmitted?.(report);
      reset();
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to update record.';
      onToast?.({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  const caseNumber = caseData?.caseNumber || caseData?.case_number || '';
  const caseName = caseData?.caseName || caseData?.case_name || '';

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title="Case Outcome Report" width={550}>
      <form onSubmit={handleSubmit}>
        {/* 1. Case Information */}
        <div className="w-full flex items-start flex-col gap-y-2">
          <span className="text-lg font-semibold">
            <b className="text-black">Case Information</b>
          </span>
          <ul className="flex flex-col text-xs">
            <li className="flex items-center gap-2">
              <span className="text-[#727B88] text-sm font-normal">Associated Case Number</span>
              <b className="font-semibold text-[#111927]">{caseNumber || '—'}</b>
            </li>
            <li className="flex items-center gap-2 pt-2">
              <span className="text-[#727B88] text-sm font-normal">Associated Title</span>
              <b className="font-semibold text-[#111927]">{caseName || '—'}</b>
            </li>
          </ul>
        </div>

        {/* 2. Rate Yourself */}
        <div className="w-full flex items-start flex-col gap-y-2 mt-8">
          <span className="text-lg font-semibold">
            <b className="text-black">Rate Yourself</b>
          </span>
          <div className="flex items-center w-full gap-2">
            <div className="w-2/3 pb-2 max-w-full overflow-auto flex items-center justify-start gap-x-2">
              <StarRow value={values.rating || 0} onChange={(v) => setValue('rating', v)} />
            </div>
          </div>
        </div>

        {/* 3. Settlement Status accordion */}
        <div className="w-full mt-8 space-y-4">
          <SettlementStatusSection values={values} setValue={setValue} errors={errors} />
        </div>

        {/* 4. Participants accordion */}
        {mergedParticipants.length > 0 && (
          <div className="w-full mt-8 space-y-4">
            {mergedParticipants.map((p) => (
              <ParticipantItem
                key={p.id}
                participant={p}
                values={values}
                setParticipantValue={setParticipantValue}
                ensureBaseValues={ensureBaseValues}
              />
            ))}
          </div>
        )}

        {/* 5. CAR Cases Outcome Report (only when caseData.is_car_case) */}
        {isCarPortal && (
          <div className="w-full mt-8 space-y-4">
            <CarSection values={values} setValue={setValue} errors={errors} />
          </div>
        )}

        {/* 6. Footer */}
        <div className="py-4 w-full mt-4">
          <button
            type="submit"
            disabled={submitting}
            className="bg-[#115E59] text-white px-5 py-2.5 rounded-md text-sm font-semibold float-right disabled:opacity-60 hover:bg-[#0f4d49]"
          >
            {submitting ? 'Saving…' : 'Save Outcome'}
          </button>
          <p className="text-xs text-center mt-2 clear-both pt-12">
            <i>You will not be able to edit the outcome later</i>
          </p>
        </div>
      </form>
    </Drawer>
  );
}
